<template>
  <v-row class="text-left full-height">
    <v-col cols="12">
      <v-card class="pa-4 rounded-lg">
        <Typography variant="h-2" class="pb-2">Developer Settings</Typography>
        <template v-if="hasVisibleTabs">
          <v-tabs :value="tab" color="brandPrimary" @change="onTabChange">
            <v-tab v-if="canSyncRoles">Roles</v-tab>
            <v-tab v-if="canManageInformSchema">Inform</v-tab>
            <v-tab v-if="canManageTaxConfig">Tax Config</v-tab>
            <v-tab v-if="canSetupSSO">SSO Setup</v-tab>
          </v-tabs>
          <v-tabs-items :value="tab">
            <v-tab-item v-if="canSyncRoles">
              <v-card-text class="pa-4">
                <RoleSync />
              </v-card-text>
            </v-tab-item>
            <v-tab-item v-if="canManageInformSchema">
              <v-card-text class="pa-4">
                <InformSettings
                  ref="informSettings"
                  :client="client"
                  @getClient="handleClientUpdated"
                />
              </v-card-text>
            </v-tab-item>
            <v-tab-item v-if="canManageTaxConfig">
              <v-card-text class="pa-4">
                <TaxConfig ref="taxConfig" :client="client" @getClient="handleClientUpdated" />
              </v-card-text>
            </v-tab-item>
            <v-tab-item v-if="canSetupSSO">
              <v-card-text class="pa-4">
                <!-- Mounted only while active: the setup link is in memory and must not outlive the tab. -->
                <SSOSetup v-if="isSSOTabActive" ref="ssoSetup" :client="client" />
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
        </template>
        <Typography v-else variant="body-2" class="grey--text text--darken-1">
          You don't have access to any tools in this section.
        </Typography>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import RoleSync from "@/components/account-management/RoleSync.vue";
import InformSettings from "@/components/account-management/InformSettings.vue";
import TaxConfig from "@/components/account-management/TaxConfig.vue";
import SSOSetup from "@/components/account-management/SSOSetup.vue";
import Typography from "@/components/generic/Typography.vue";
import featureFlags from "@/mixins/featureFlags";
import { mapState } from "vuex";

export default {
  name: "DeveloperSettings",
  mixins: [featureFlags],
  props: {
    client: {
      type: Object,
      required: false,
    },
  },
  components: {
    RoleSync,
    InformSettings,
    TaxConfig,
    SSOSetup,
    Typography,
  },
  data() {
    return {
      tab: 0,
    };
  },
  computed: {
    ...mapState(["permissions"]),
    /** Original Role Sync gate (Account Management). */
    canSyncRoles() {
      return (this.permissions || []).includes("auth0:create:migration");
    },
    canManageInformSchema() {
      return (this.permissions || []).includes("inform:manage:schema");
    },
    /** Scope is granted alongside `secrets:write:secret`, which the tab's endpoints require. */
    canManageTaxConfig() {
      return (
        this.isTaxInfoEnabled() && (this.permissions || []).includes("secrets:manage:tax-config")
      );
    },
    canSetupSSO() {
      return (
        (this.permissions || []).includes("vue:read:developer-settings") &&
        this.isSSOSetupTicketsEnabled()
      );
    },
    visibleTabCount() {
      return [
        this.canSyncRoles,
        this.canManageInformSchema,
        this.canManageTaxConfig,
        this.canSetupSSO,
      ].filter(Boolean).length;
    },
    hasVisibleTabs() {
      return this.visibleTabCount > 0;
    },
    // SSO Setup is always the last visible tab.
    isSSOTabActive() {
      return this.canSetupSSO && this.tab === this.visibleTabCount - 1;
    },
  },
  watch: {
    canSetupSSO(visible) {
      // `tab` is an index; SSO Setup is last, so it can only fall out of range when hidden.
      if (!visible && this.tab >= this.visibleTabCount) this.tab = 0;
    },
  },
  methods: {
    handleClientUpdated(payload) {
      this.$emit("getClient", payload);
    },
    async onTabChange(newTab) {
      if (newTab === this.tab) return;
      const canLeave = await this.confirmCanLeave();
      if (canLeave) this.tab = newTab;
      // When `canLeave` is false we intentionally don't update `tab`. The
      // v-tabs is fully controlled via `:value` so the active tab snaps back
      // to the prior value on the next render tick.
    },
    /**
     * Public — called by AccountManagementV2 (outer tab change + route leave).
     * Delegates to whichever children are mounted.
     */
    async confirmCanLeave() {
      const children = [
        this.$refs.informSettings,
        this.$refs.taxConfig,
        this.$refs.ssoSetup,
      ].filter(Boolean);
      for (const child of children) {
        const canLeave = await child.confirmCanLeave();
        if (!canLeave) return false;
      }
      return true;
    },
  },
};
</script>

<style scoped></style>
